import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, Select, MenuItem, InputLabel, FormControl
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContextNavBar from '../components/ContextNavBar';
import ModuleHeader from '../components/ModuleHeader';

const LIME = '#00F1A7';
const DARK = '#1C1C1E';

function safeToString(val) {
  if (val == null) return '-';
  if (typeof val === 'string' || typeof val === 'number') return val;
  if (Array.isArray(val)) return '[array]';
  if (typeof val === 'object' && val.$$typeof) return '[elemento React]';
  if (typeof val === 'object') {
    if (val.nome) return val.nome;
    if (val.data) return val.data;
    try { return JSON.stringify(val); } catch { return '[objeto]'; }
  }
  return '[valor não suportado]';
}

function Frequencias() {
  const [openModal, setOpenModal] = useState(false);
  const [frequencias, setFrequencias] = useState([]);
  const [alunoId, setAlunoId] = useState('');
  const [aulaId, setAulaId] = useState('');
  const [presente, setPresente] = useState(true);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [alunos, setAlunos] = useState([]);
  const [aulas, setAulas] = useState([]);

  const fetchFrequencias = async () => {
    try {
      const res = await api.get('/frequencias/');
      setFrequencias(res.data);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar frequências');
    }
  };

  useEffect(() => {
    const carregar = async () => {
      setLoading(true);
      setErro('');
      try {
        const [resFreq, resAlunos, resAulas] = await Promise.all([
          api.get('/frequencias/'),
          api.get('/alunos/'),
          api.get('/aulas/')
        ]);
        setFrequencias(resFreq.data);
        setAlunos(resAlunos.data);
        setAulas(resAulas.data);
      } catch (err) {
        setErro(errorMsg(err) || 'Erro ao carregar dados');
      }
      setLoading(false);
    };
    carregar();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setErro(''); setSucesso('');
    if (!alunoId || !aulaId) {
      setErro('Preencha aluno e aula');
      return;
    }
    try {
      await api.post('/frequencias/', { aluno_id: alunoId, aula_id: aulaId, presente });
      setAlunoId(''); setAulaId(''); setPresente(true);
      setSucesso('Frequência registrada!');
      setOpenModal(false);
      fetchFrequencias();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro de conexão');
    }
  };

  const handleEdit = (id) => {
    const freq = frequencias.find(f => f.id === id);
    setEditId(id);
    setEditData({
      aluno_id: freq.aluno_id,
      aula_id: freq.aula_id,
      presente: freq.presente
    });
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleEditSave = async (id) => {
    setErro(''); setSucesso('');
    try {
      const body = {};
      if (editData.aluno_id) body.aluno_id = editData.aluno_id;
      if (editData.aula_id) body.aula_id = editData.aula_id;
      if (typeof editData.presente === 'boolean') body.presente = editData.presente;
      await api.put(`/frequencias/${id}`, body);
      setEditId(null);
      setEditData({});
      setSucesso('Frequência atualizada!');
      fetchFrequencias();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro de conexão');
    }
  };

  const handleEditCancel = () => {
    setEditId(null);
    setEditData({});
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/frequencias/${id}`);
      fetchFrequencias();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao excluir frequência');
    }
  };

  const getNomeAluno = (id) => {
    const a = alunos.find(a => a.id === id);
    return a ? a.nome : id;
  };

  const getAulaLabel = (id) => {
    const a = aulas.find(a => a.id === id);
    return a ? `Aula ${a.id} - ${a.data}` : id;
  };

  if (loading) return <><Header /><ContextNavBar context="startfy" /><Container sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Container><Footer /></>;
  if (erro && frequencias.length === 0) return <><Header /><ContextNavBar context="startfy" /><Container sx={{ py: 4 }}><Alert severity="error">{erro}</Alert></Container><Footer /></>;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: { xs: 1, md: 2 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: DARK, fontSize: { xs: '1.3rem', md: '2rem' } }}>
          Frequências
        </Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => setOpenModal(true)}
          sx={{ bgcolor: LIME, color: DARK, fontWeight: 600, '&:hover': { filter: 'brightness(0.9)' } }}
        >
          Registrar
        </Button>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
      {sucesso && <Alert severity="success" sx={{ mb: 2 }}>{sucesso}</Alert>}

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Aluno</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Aula</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Situação</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 120, whiteSpace: 'nowrap' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {frequencias.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ color: '#999', py: 4 }}>
                  Nenhuma frequência registrada
                </TableCell>
              </TableRow>
            ) : (
              frequencias.map((freq) => (
                <TableRow
                  key={freq.id}
                  hover
                  sx={{
                    bgcolor: editId === freq.id ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
                  }}
                >
                  {editId === freq.id ? (
                    <>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <InputLabel id={`aluno-edit-label-${freq.id}`}>Aluno</InputLabel>
                          <Select
                            labelId={`aluno-edit-label-${freq.id}`}
                            value={editData.aluno_id || ''}
                            label="Aluno"
                            onChange={e => handleEditChange('aluno_id', e.target.value)}
                          >
                            {alunos.map(aluno => (
                              <MenuItem key={aluno.id} value={aluno.id}>{aluno.nome}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <InputLabel id={`aula-edit-label-${freq.id}`}>Aula</InputLabel>
                          <Select
                            labelId={`aula-edit-label-${freq.id}`}
                            value={editData.aula_id || ''}
                            label="Aula"
                            onChange={e => handleEditChange('aula_id', e.target.value)}
                          >
                            {aulas.map(aula => (
                              <MenuItem key={aula.id} value={aula.id}>Aula {aula.id} - {aula.data}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={!!editData.presente}
                          onChange={e => handleEditChange('presente', e.target.checked)}
                        />
                        Presente
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Salvar">
                            <IconButton color="primary" size="small" onClick={() => handleEditSave(freq.id)}>
                              <SaveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar">
                            <IconButton color="inherit" size="small" onClick={handleEditCancel}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ fontWeight: 600 }}>{getNomeAluno(freq.aluno_id)}</TableCell>
                      <TableCell>{getAulaLabel(freq.aula_id)}</TableCell>
                      <TableCell>{safeToString(freq.presente ? 'Presente' : 'Falta')}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Editar">
                            <IconButton size="small" color="info" onClick={() => handleEdit(freq.id)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" color="error" onClick={() => handleDelete(freq.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: DARK }}>Nova Frequência</DialogTitle>
        <Box component="form" onSubmit={handleAdd}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="aluno-select-label">Aluno</InputLabel>
              <Select
                labelId="aluno-select-label"
                value={alunoId}
                label="Aluno"
                onChange={e => setAlunoId(e.target.value)}
              >
                {alunos.map(aluno => (
                  <MenuItem key={aluno.id} value={aluno.id}>{aluno.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="aula-select-label">Aula</InputLabel>
              <Select
                labelId="aula-select-label"
                value={aulaId}
                label="Aula"
                onChange={e => setAulaId(e.target.value)}
              >
                {aulas.map(aula => {
                  const jaMarcada = frequencias.some(f => f.aluno_id === alunoId && f.aula_id === aula.id);
                  return (
                    <MenuItem key={aula.id} value={aula.id} disabled={jaMarcada}>
                      Aula {aula.id} - {aula.data} {jaMarcada ? '(já marcada)' : ''}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox checked={presente} onChange={e => setPresente(e.target.checked)} /> Presente
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{ bgcolor: LIME, color: DARK, fontWeight: 600, '&:hover': { filter: 'brightness(0.9)' } }}
            >
              Registrar
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Container>
  );
}

export default function FrequenciasPageWrapper(props) {
  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };
  return (
    <>
      <Header onLogout={handleLogout} />
      <ContextNavBar context="startfy" />
      <Frequencias {...props} />
      <Footer />
    </>
  );
}
